/**
 * SettingsScene - Game Settings Screen
 *
 * Features:
 * - Sound effects toggle (on/off) with icon
 * - Music toggle (on/off) with icon
 * - Reset progress button with confirmation dialog
 * - Back button navigation
 * - Settings saved immediately via SaveSystem
 *
 * Per PRD section 5.2 and Task #27 specifications
 */

import Phaser from 'phaser';
import {
  COLOR_CSS,
  COLORS,
  FONTS,
  TEXT_SIZES,
  GAME_WIDTH,
  GAME_HEIGHT,
  DURATIONS,
  SAFE_AREA,
  SPACING,
} from '../config';
import { Button } from '../ui/Button';
import { saveSystem } from '../systems/SaveSystem';
import { audioSystem } from '../systems/AudioSystem';
import { logSceneEvent } from '../utils/automation';
import { createCommandDeckBackground, createSceneHeader } from '../ui/SceneChrome';

export class SettingsScene extends Phaser.Scene {
  private confirmDialog: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: 'SettingsScene' });
  }

  create(): void {
    logSceneEvent('SettingsScene');
    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);
    createCommandDeckBackground(this);

    this.createHeader();
    this.createToggles();
    this.createResetButton();
    this.createBackButton();
  }

  // ===========================================================================
  // CREATION
  // ===========================================================================

  /**
   * Creates the header title
   */
  private createHeader(): void {
    createSceneHeader(this, {
      title: 'Systems',
      kicker: 'Command deck // preferences',
      y: SAFE_AREA.top + 44,
    });
  }

  /**
   * Creates the sound and music toggle rows
   */
  private createToggles(): void {
    const startY = SAFE_AREA.top + 120;
    const rowHeight = 80;

    // Sound Effects toggle
    this.createToggleRow(
      startY,
      'Sound Effects',
      saveSystem.isSoundEnabled(),
      (enabled) => this.onSoundToggle(enabled)
    );

    // Music toggle
    this.createToggleRow(
      startY + rowHeight,
      'Music',
      saveSystem.isMusicEnabled(),
      (enabled) => this.onMusicToggle(enabled)
    );
  }

  /**
   * Creates a toggle row with label and switch
   */
  private createToggleRow(
    y: number,
    label: string,
    initialState: boolean,
    onToggle: (enabled: boolean) => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(0, y);
    const rowWidth = GAME_WIDTH - SPACING.space5 * 2;
    const rowX = SPACING.space5;

    // Row background
    const bg = this.add.graphics();
    bg.fillStyle(COLORS.bgOverlay, 0.5);
    bg.fillRoundedRect(rowX, 0, rowWidth, 64, 12);
    container.add(bg);

    // Label
    const labelText = this.add.text(
      rowX + 20,
      32,
      label,
      {
        fontFamily: FONTS.body,
        fontSize: `${TEXT_SIZES.body}px`,
        color: COLOR_CSS.uiText,
      }
    ).setOrigin(0, 0.5);
    container.add(labelText);

    // Toggle switch
    const switchX = rowX + rowWidth - 50;
    const switchContainer = this.createToggleSwitch(switchX, 32, initialState, onToggle);
    container.add(switchContainer);

    return container;
  }

  /**
   * Creates a toggle switch component
   */
  private createToggleSwitch(
    x: number,
    y: number,
    initialState: boolean,
    onToggle: (enabled: boolean) => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    let isEnabled = initialState;

    const switchWidth = 56;
    const switchHeight = 32;
    const knobSize = 24;
    const padding = 4;

    // Background track
    const track = this.add.graphics();
    container.add(track);

    // Knob
    const knob = this.add.graphics();
    container.add(knob);

    // Function to render current state
    const renderState = (enabled: boolean, animate: boolean = false) => {
      const trackColor = enabled ? COLORS.gate : COLORS.uiButtonSecondary;
      const knobX = enabled
        ? switchWidth / 2 - padding - knobSize / 2
        : -switchWidth / 2 + padding + knobSize / 2;

      track.clear();
      track.fillStyle(trackColor, 1);
      track.fillRoundedRect(-switchWidth / 2, -switchHeight / 2, switchWidth, switchHeight, switchHeight / 2);

      if (animate) {
        this.tweens.add({
          targets: knob,
          x: knobX,
          duration: 150,
          ease: 'Power2',
        });
      } else {
        knob.x = knobX;
      }

      knob.clear();
      knob.fillStyle(0xFFFFFF, 1);
      knob.fillCircle(0, 0, knobSize / 2);
    };

    // Initial render
    renderState(isEnabled);

    // Make interactive
    const hitArea = this.add.rectangle(0, 0, switchWidth, switchHeight, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerdown', () => {
      isEnabled = !isEnabled;
      renderState(isEnabled, true);
      onToggle(isEnabled);
      audioSystem.play('button_tap');
    });

    return container;
  }

  /**
   * Creates the reset progress button
   */
  private createResetButton(): void {
    const buttonY = GAME_HEIGHT / 2 + 40;

    // Warning text above button
    this.add.text(
      GAME_WIDTH / 2,
      buttonY - 40,
      'Danger Zone',
      {
        fontFamily: FONTS.display,
        fontSize: `${TEXT_SIZES.caption}px`,
        color: COLOR_CSS.enemy,
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);

    // Reset button container with custom styling
    const btnContainer = this.add.container(GAME_WIDTH / 2, buttonY);

    const btnWidth = 200;
    const btnHeight = 48;

    // Button background (danger red styling)
    const btnBg = this.add.graphics();
    btnBg.fillStyle(COLORS.enemy, 0.2);
    btnBg.lineStyle(2, COLORS.enemy, 1);
    btnBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 12);
    btnBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 12);
    btnContainer.add(btnBg);

    // Button text
    const btnText = this.add.text(0, 0, 'Reset Progress', {
      fontFamily: FONTS.body,
      fontSize: `${TEXT_SIZES.body}px`,
      color: COLOR_CSS.enemy,
    }).setOrigin(0.5);
    btnContainer.add(btnText);

    // Make interactive
    btnContainer.setSize(btnWidth, btnHeight);
    btnContainer.setInteractive({ useHandCursor: true });

    btnContainer.on('pointerdown', () => {
      this.tweens.add({
        targets: btnContainer,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 100,
        ease: 'Power2',
      });
    });

    btnContainer.on('pointerup', () => {
      this.tweens.add({
        targets: btnContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Back.easeOut',
        onComplete: () => {
          audioSystem.play('button_tap');
          this.showConfirmDialog();
        },
      });
    });

    btnContainer.on('pointerout', () => {
      this.tweens.add({
        targets: btnContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Power2',
      });
    });
  }

  /**
   * Creates the back button
   */
  private createBackButton(): void {
    new Button(this, {
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT - SAFE_AREA.bottom - 50,
      text: 'Back',
      variant: 'secondary',
      width: 120,
      height: 44,
      onClick: () => this.goBack(),
    });
  }

  // ===========================================================================
  // CONFIRMATION DIALOG
  // ===========================================================================

  /**
   * Shows the reset confirmation dialog
   */
  private showConfirmDialog(): void {
    if (this.confirmDialog) return;

    this.confirmDialog = this.add.container(0, 0);
    this.confirmDialog.setDepth(100);

    // Overlay background
    const overlay = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x000000,
      0.8
    );
    overlay.setInteractive(); // Block clicks behind
    this.confirmDialog.add(overlay);

    // Dialog box
    const dialogWidth = GAME_WIDTH - SPACING.space6 * 2;
    const dialogHeight = 220;
    const dialogX = (GAME_WIDTH - dialogWidth) / 2;
    const dialogY = GAME_HEIGHT / 2 - dialogHeight / 2;

    const dialogBg = this.add.graphics();
    dialogBg.fillStyle(COLORS.bgMenu, 1);
    dialogBg.fillRoundedRect(dialogX, dialogY, dialogWidth, dialogHeight, 16);
    dialogBg.lineStyle(2, COLORS.enemy, 0.5);
    dialogBg.strokeRoundedRect(dialogX, dialogY, dialogWidth, dialogHeight, 16);
    this.confirmDialog.add(dialogBg);

    // Warning icon/text
    const warningText = this.add.text(
      GAME_WIDTH / 2,
      dialogY + 30,
      'RESET CAMPAIGN?',
      {
        fontFamily: FONTS.display,
        fontSize: `${TEXT_SIZES.title}px`,
        color: COLOR_CSS.enemy,
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);
    this.confirmDialog.add(warningText);

    // Description
    const descText = this.add.text(
      GAME_WIDTH / 2,
      dialogY + 75,
      'This will delete ALL your progress:\n• Level unlocks\n• High scores\n• Statistics',
      {
        fontFamily: FONTS.body,
        fontSize: `${TEXT_SIZES.caption}px`,
        color: COLOR_CSS.uiTextSecondary,
        align: 'center',
        lineSpacing: 4,
      }
    ).setOrigin(0.5, 0);
    this.confirmDialog.add(descText);

    // Cannot be undone warning
    const undoText = this.add.text(
      GAME_WIDTH / 2,
      dialogY + 145,
      'This cannot be undone!',
      {
        fontFamily: FONTS.body,
        fontSize: `${TEXT_SIZES.caption}px`,
        color: COLOR_CSS.enemy,
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);
    this.confirmDialog.add(undoText);

    // Buttons container
    const buttonsY = dialogY + dialogHeight - 45;
    const buttonSpacing = 80;

    // Cancel button
    const cancelBtn = new Button(this, {
      x: GAME_WIDTH / 2 - buttonSpacing,
      y: buttonsY,
      text: 'Cancel',
      variant: 'secondary',
      width: 110,
      height: 44,
      onClick: () => this.hideConfirmDialog(),
    });
    this.confirmDialog.add(cancelBtn);

    // Confirm button (styled as danger)
    const confirmContainer = this.add.container(GAME_WIDTH / 2 + buttonSpacing, buttonsY);

    const confirmBg = this.add.graphics();
    confirmBg.fillStyle(COLORS.enemy, 1);
    confirmBg.fillRoundedRect(-55, -22, 110, 44, 12);
    confirmContainer.add(confirmBg);

    const confirmText = this.add.text(0, 0, 'Reset', {
      fontFamily: FONTS.display,
      fontSize: `${TEXT_SIZES.body}px`,
      color: '#FFFFFF',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    confirmContainer.add(confirmText);

    confirmContainer.setSize(110, 44);
    confirmContainer.setInteractive({ useHandCursor: true });

    confirmContainer.on('pointerdown', () => {
      this.tweens.add({
        targets: confirmContainer,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 100,
        ease: 'Power2',
      });
    });

    confirmContainer.on('pointerup', () => {
      this.tweens.add({
        targets: confirmContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Back.easeOut',
        onComplete: () => {
          this.confirmReset();
        },
      });
    });

    confirmContainer.on('pointerout', () => {
      this.tweens.add({
        targets: confirmContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Power2',
      });
    });

    this.confirmDialog.add(confirmContainer);

    // Fade in dialog
    this.confirmDialog.setAlpha(0);
    this.tweens.add({
      targets: this.confirmDialog,
      alpha: 1,
      duration: 200,
      ease: 'Power2',
    });
  }

  /**
   * Hides the confirmation dialog
   */
  private hideConfirmDialog(): void {
    if (!this.confirmDialog) return;

    this.tweens.add({
      targets: this.confirmDialog,
      alpha: 0,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        if (this.confirmDialog) {
          this.confirmDialog.destroy();
          this.confirmDialog = null;
        }
      },
    });
  }

  /**
   * Confirms the reset and clears all progress
   */
  private confirmReset(): void {
    audioSystem.play('button_tap');

    // Reset all save data
    saveSystem.reset();

    // Hide dialog
    this.hideConfirmDialog();

    // Show confirmation feedback
    const confirmMsg = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      'Progress Reset!',
      {
        fontFamily: FONTS.display,
        fontSize: `${TEXT_SIZES.title}px`,
        color: COLOR_CSS.gate,
        fontStyle: 'bold',
      }
    ).setOrigin(0.5).setAlpha(0);

    this.tweens.add({
      targets: confirmMsg,
      alpha: 1,
      y: GAME_HEIGHT / 2 - 20,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.time.delayedCall(1000, () => {
          this.tweens.add({
            targets: confirmMsg,
            alpha: 0,
            y: GAME_HEIGHT / 2 - 40,
            duration: 300,
            onComplete: () => confirmMsg.destroy(),
          });
        });
      },
    });

    // Update toggles to reflect reset state (settings reset to enabled)
    this.scene.restart();
  }

  // ===========================================================================
  // TOGGLE HANDLERS
  // ===========================================================================

  /**
   * Handles sound toggle changes
   */
  private onSoundToggle(enabled: boolean): void {
    audioSystem.setSoundEnabled(enabled);
  }

  /**
   * Handles music toggle changes
   */
  private onMusicToggle(enabled: boolean): void {
    audioSystem.setMusicEnabled(enabled);
  }

  // ===========================================================================
  // NAVIGATION
  // ===========================================================================

  /**
   * Returns to title screen
   */
  private goBack(): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('TitleScene');
    });
  }
}
